import { useRef, useState, useEffect, useCallback } from 'react';
import type { Book } from '@/mocks/books';

interface BookSliderProps {
  books: Book[];
}

export default function BookSlider({ books }: BookSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const current = el.scrollLeft;
    setProgress(maxScroll > 0 ? current / maxScroll : 0);
    setCanScrollLeft(current > 1);
    setCanScrollRight(current < maxScroll - 1);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = trackRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-slider-card]')?.clientWidth || 280;
    const gap = 24;
    const scrollAmount = cardWidth + gap;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  if (books.length === 0) return null;

  return (
    <div className="relative">
      {/* ── Navigation arrows ── */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50 border border-background-200 flex items-center justify-center transition-all hover:bg-background-100 hover:border-secondary-300 shadow-sm"
          aria-label="向左滑動"
          style={{ transform: 'translateY(-50%) translateX(-50%)' }}
        >
          <i className="ri-arrow-left-s-line text-lg text-foreground-600" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50 border border-background-200 flex items-center justify-center transition-all hover:bg-background-100 hover:border-secondary-300 shadow-sm"
          aria-label="向右滑動"
          style={{ transform: 'translateY(-50%) translateX(50%)' }}
        >
          <i className="ri-arrow-right-s-line text-lg text-foreground-600" />
        </button>
      )}

      {/* ── Scroll track ── */}
      <div
        ref={trackRef}
        className="flex gap-5 md:gap-6 overflow-x-auto pb-6 pt-2 px-1"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          scrollSnapType: 'x mandatory',
        }}
      >
        <style>{`
          .book-slider-track::-webkit-scrollbar { display: none; }
        `}</style>
        {books.map((book) => (
          <div
            key={book.slug}
            data-slider-card
            className="flex-shrink-0 book-slider-track"
            style={{
              scrollSnapAlign: 'start',
              width: 'min(100%, 320px)',
            }}
          >
            <a
              href="https://catalog.sonbook.net"
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-background-50 border border-background-200 rounded-lg overflow-hidden transition-all duration-300 hover:border-secondary-300 hover:shadow-sm cursor-pointer"
            >
              {/* Cover */}
              <div className="relative aspect-[3/4] bg-background-100 overflow-hidden">
                {book.coverImage ? (
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-book-2-line text-4xl text-background-300" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4 md:p-5">
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 leading-snug mb-1.5 line-clamp-2">
                  {book.title}
                </h3>
                <p className="text-xs text-foreground-500 mb-1">{book.author}</p>
                {book.category && (
                  <span className="inline-block text-[11px] font-label px-2 py-0.5 rounded-sm mb-2" style={{ backgroundColor: 'oklch(var(--secondary-100))', color: 'oklch(var(--secondary-800))' }}>
                    {book.category}
                  </span>
                )}
                <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3 mb-3">
                  {book.description}
                </p>
                <div className="flex items-center gap-1 text-xs font-medium whitespace-nowrap" style={{ color: 'oklch(var(--primary-600))' }}>
                  <span>前往完整書目</span>
                  <i className="ri-arrow-right-line text-[10px] transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>

      {/* ── Progress bar ── */}
      <div className="relative h-[2px] bg-background-200 rounded-full overflow-hidden mx-1">
        <div
          className="absolute top-0 left-0 h-full bg-primary-500 rounded-full transition-all duration-300"
          style={{ width: `${Math.max(8, progress * 100)}%` }}
        />
      </div>

      {/* Page indicator */}
      <div className="flex items-center justify-center gap-2 mt-3">
        <span className="font-mono text-[10px] tracking-wider text-foreground-400">
          {books.length} 本書籍
        </span>
      </div>
    </div>
  );
}